import styled from 'styled-components';
import { useCallback, useEffect, useState } from 'react';
import { TabProps } from '../../../../blocks/tabs-vertical/TabsVertical';
import Button from '../../../../components/button/Button';
import FilePreview from '../../../../components/file-preview/FilePreview';
import AttachmentService from '../../../../services/AttachmentService';
import { AttachmentDTO } from '../../../../api/api';
import NotificationService from '../../../../services/NotificationService';
import { useConfirmationDialog } from '../../../../hooks/useConfirmationDialog';
import { getGuid } from '../../../../utils/guidGenerating';
import { log } from '../../../../utils/logging/log';
import { renameFile, isImageTooSmall, MIN_IMAGE_DIMENSION_PX } from '../../../../utils/fileUtils';
import { DownloadInfo, ImageType } from '../../../../models/DownloadInfo';
import ImageGalleryDialog from '../../../../components/file-preview/ImageGalleryDialog';
import ImageService from '../../../../services/ImageService';

const AcceptString = '.pdf,.png,.jpeg,.xlsx,.pptx,.docx,.svg,.doc,.xls,.ppt,.jpg,.mov';

interface Props extends TabProps {
  taskId: string;
  onFileUploaded?: () => void;
  onFileRemoved?: () => void;
}

const TaskFilesStep = (props: Props) => {
  const { taskId, onFileUploaded, onFileRemoved } = props;
  const [attachments, setAttachments] = useState<AttachmentDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [images, setImages] = useState<string[]>([]);

  const { getConfirmation } = useConfirmationDialog();

  const fetchAttachments = useCallback(() => {
    setIsLoading(true);
    AttachmentService.getAttachments(parseInt(taskId))
      .then((result) => {
        setAttachments(result);
        setIsLoading(false);
      })
      .catch((error) => {
        log(error);
        setIsLoading(false);
      });
  }, [taskId]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const handleUpload = async (files: FileList | null) => {
    if (!files) return;

    const selectedFiles = Array.from(files);
    const tooSmallFlags = await Promise.all(selectedFiles.map((file) => isImageTooSmall(file)));
    const tooSmallImages = selectedFiles.filter((_, i) => tooSmallFlags[i]);
    const validFiles = selectedFiles.filter((_, i) => !tooSmallFlags[i]);

    if (tooSmallImages.length > 0) {
      NotificationService.error(
        `Billedet skal være større end ${MIN_IMAGE_DIMENSION_PX}×${MIN_IMAGE_DIMENSION_PX} pixels for at sikre tilstrækkelig billedkvalitet - følgende blev ikke gemt: ${tooSmallImages
          .map((file) => file.name)
          .join(', ')}`
      );
    }

    if (validFiles.length === 0) return;

    setIsLoading(true);

    const filesToUpload: File[] = [];
    validFiles.forEach((file) => {
      if (file.name === 'image.jpg') file = renameFile(file, getGuid());
      filesToUpload.push(file);
    });

    if (filesToUpload.length > 0) {
      const promises = filesToUpload.map((file) => AttachmentService.postAttachment(parseInt(taskId), file));

      Promise.allSettled(promises)
        .then((results) => {
          const rejectedPromises = results.filter((r) => r.status === 'rejected');
          onFileUploaded && onFileUploaded();

          if (rejectedPromises.length > 0) {
            NotificationService.error(`Der opstod fejl under upload af en eller flere filer`);
          }
        })
        .catch((e) => {
          log(e);
          NotificationService.error(`Der opstod fejl under upload af en eller flere filer`);
        })
        .finally(() => {
          fetchAttachments();
        });
    }
  };

  const handleDelete = async (name: string) => {
    const confirmation = await getConfirmation({
      headerText: 'Er du sikker?',
      bodyText: `Vil du slette filen ${name}?`,
      declineButtonText: 'Fortryd',
      confirmButtonText: 'Bekræft'
    });

    if (confirmation === 'confirm') {
      setIsLoading(true);
      AttachmentService.deleteAttachment(parseInt(taskId), name)
        .then(() => {
          NotificationService.success(`${name} blev slettet`);
          onFileRemoved && onFileRemoved();
        })
        .catch((error) => {
          NotificationService.error(`Kunne ikke slette filen. ${error}`);
        })
        .finally(() => {
          fetchAttachments();
        });
    }
  };

  const handleDownload = useCallback(
    (filename: string): DownloadInfo => {
      return { imageType: ImageType.AttachmentDownloadInfo, taskId: +taskId, filename };
    },
    [taskId]
  );

  const getThumbnail = useCallback(
    async (fileName: string): Promise<string> => {
      const filename = fileName.substring(0, fileName.lastIndexOf('.'));
      return AttachmentService.getThumbnail(parseInt(taskId), filename)
        .then((res) => URL.createObjectURL(res.data))
        .catch((err) => {
          log(err);
          return '';
        });
    },
    [taskId]
  );

  // Filtrer kun billeder og lav array med filnavn + URL (brug thumbnail eller evt. url fra attachment hvis du har)
  const imageAttachments = attachments
    .filter((att) => att.isImage)
    .map((att) => ({
      filename: att.filename ?? '',
      // Hvis du har url i attachment, brug den her, ellers lav evt. en blob URL:
      url: '' // Tilpas om nødvendigt
    }));

  // Åbn galleri og find valgt index baseret på filnavn
  const openGallery = (filename: string) => {
    const index = imageAttachments.findIndex((img) => img.filename === filename);
    if (index !== -1) {
      setGalleryIndex(index);
      setGalleryOpen(true);
    }
  };

  useEffect(() => {
    if (!attachments.length) return;

    const imageFiles = attachments.filter((att) => att.isImage);

    const promises = imageFiles.map((attachment) =>
      ImageService.downloadImage({
        filename: attachment.filename,
        taskId: parseInt(taskId),
        imageType: ImageType.AttachmentDownloadInfo
      })
    );

    Promise.allSettled(promises).then((data) => {
      const newImages = data.filter((d) => d.status === 'fulfilled').map((d) => URL.createObjectURL((d as any).value.data));

      setImages(newImages);
    });
  }, [attachments, taskId]);

  return (
    <Container>
      <StyledHeader>Tilføjede dokumenter</StyledHeader>
      <UploadedFilesContainer>
        {attachments.map((attachment) => (
          <FileContainer key={attachment.filename}>
            <FilePreview
              name={attachment.filename ?? ''}
              handleRemove={() => handleDelete(attachment.filename ?? '')}
              handleDownload={(fileName: string) => handleDownload(fileName)}
              handleThumbnail={() => getThumbnail(attachment.filename ?? '')}
              date={attachment.createdOn ?? ''}
              uploadedBy={attachment.uploadedBy ?? ''}
              isImage={attachment.isImage}
              onClick={() => {
                if (attachment.isImage) {
                  openGallery(attachment.filename ?? '');
                }
              }}
            />
          </FileContainer>
        ))}
      </UploadedFilesContainer>
      <ButtonContainer>
        <input
          style={{ display: 'none' }}
          type="file"
          id="image-upload"
          multiple
          accept={AcceptString}
          onChange={(e) => handleUpload(e.target.files)}
        />
        <input
          capture="environment"
          style={{ display: 'none' }}
          type="file"
          id="image-upload-camera"
          multiple
          accept="image/jpeg, .jpg, .png"
          onChange={(e) => handleUpload(e.target.files)}
        />

        <label htmlFor="image-upload">
          <Button isLoading={isLoading} variant="secondary" element="span">
            Upload filer
          </Button>
        </label>
      </ButtonContainer>

      {galleryOpen && (
        <ImageGalleryDialog initialIndex={galleryIndex} onClose={() => setGalleryOpen(false)} images={images} />
      )}
    </Container>
  );
};

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

export const StyledHeader = styled.h3`
  margin: 20px 0px;
`;
export const UploadedFilesContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  margin-bottom: 1rem;
  border: 1px solid lightgrey;
  padding: 10px;
  border-radius: 20px;
`;

export const FileContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.palette.grey.black20};
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
`;

export default TaskFilesStep;
